import { notification, Table, TableProps, Typography } from '@pankod/refine-antd';
import { Group, SearchNew } from 'components/search';
import { IReport, PaginationHandler } from 'interfaces';
import { useCallback, useEffect, useState } from 'react';
import { SearchIndex } from 'services/meilisearch';
import { getPurchaseReports, getSalesReports, IReportResponse } from 'services/report';
import { formatCurrency } from 'utilities/shared';
import styles from './style.module.css';
import CustomTableWithPagination from 'components/table/custom-table';

const { Text } = Typography;

type ReportsTableProps = {
  type: 'purchase' | 'sales';
  date: {
    startDate: string;
    endDate: string;
    tagIds?: string[];
    categoryId?: string;
  };
};

export const ReportsTable: React.FC<ReportsTableProps> = (props) => {
  const [reports, setReports] = useState<IReport[]>([]);
  const [totalReports, setTotalReports] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchDataOnMountCallback = useCallback(async () => {
    setIsLoading(true);
    try {
      let reportResponse = {} as IReportResponse;
      if (props.type === 'purchase') {
        reportResponse = await getPurchaseReports({
          ...props.date,
          searchString: searchText,
          start: page,
          limit,
        });
      }

      if (props.type === 'sales') {
        reportResponse = await getSalesReports({
          ...props.date,
          searchString: searchText,
          start: page,
          limit,
        });
      }

      setReports(reportResponse.data);
      setTotalReports(reportResponse.total);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  }, [props.type, props.date, searchText, page, limit]);

  useEffect(() => {
    fetchDataOnMountCallback();
  }, [fetchDataOnMountCallback]);

  const onSelectOption = async (searchText: string, _ids?: string[]) => {
    setSearchText(searchText);
    setPage(1);
  };

  const onTableChange: TableProps<IReport>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onPageChange: PaginationHandler = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  const title = props.type === 'purchase' ? 'Supplier Name' : 'Client Name';
  const searchPlaceholder = `Filter ${props.type === 'purchase' ? 'Supplier' : 'Client'}, Product`;

  const searchIndex = props.type === 'purchase' ? SearchIndex.Purchase : SearchIndex.Sales;

  const searchGroup: Group[] =
    props.type === 'purchase' ? ['supplier', 'product'] : ['client', 'product'];

  return (
    <>
      <SearchNew
        placeholder={searchPlaceholder}
        size="middle"
        className={styles['filter-section__search']}
        index={searchIndex}
        groups={searchGroup}
        onSelectOption={onSelectOption}
        onReset={fetchDataOnMountCallback}
      />
      <CustomTableWithPagination
        total={totalReports}
        page={page}
        limit={limit}
        onPageChange={onPageChange}
      >
        <Table
          dataSource={reports || []}
          rowKey="id"
          scroll={{ x: true }}
          loading={isLoading}
          onChange={onTableChange}
          // pagination={{
          //   defaultPageSize: 10,
          //   showSizeChanger: true,
          //   total: totalReports,
          //   current: page,
          // }}
          pagination={false}
        >
          <Table.Column<IReport>
            dataIndex="date"
            title="Date"
            render={(_, record): React.ReactNode => {
              const date = new Date(record.date).toLocaleDateString('en-GB');
              return <Text>{date}</Text>;
            }}
          />
          <Table.Column
            dataIndex="id"
            title={`${props.type === 'purchase' ? 'Purchase ID' : 'Sales ID'}`}
          />
          <Table.Column dataIndex="name" title={title} />
          <Table.Column<IReport>
            dataIndex="amount"
            title="Amount"
            render={(_, record) => formatCurrency(record.amount)}
          />
        </Table>
      </CustomTableWithPagination>
    </>
  );
};
